import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../common/Card';
import { getSubscriptionIcon, getCategoryColor } from '../../utils/iconUtils';
import { Button } from '../common/Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getCurrencySymbol } from '../../config/currencies';

export const UpcomingPayments = ({ subscriptions }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 2;

    // Sort subscriptions by next_billing_date and filter active ones
    const sortedSubscriptions = [...subscriptions]
        .filter(sub => sub.next_billing_date && sub.status === 'active')
        .sort((a, b) =>
            new Date(a.next_billing_date) - new Date(b.next_billing_date)
        );

    const totalPages = Math.ceil(sortedSubscriptions.length / itemsPerPage);
    const currentSubscriptions = sortedSubscriptions.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
    );

    const formatCost = (subscription) => {
        const subscriptionCurrency = subscription.currency || 'USD';
        const symbol = getCurrencySymbol(subscriptionCurrency);
        return `${symbol}${subscription.cost.toFixed(2)}`;
    };

    const handlePrevPage = () => {
        setCurrentPage(prev => Math.max(0, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Upcoming Payments</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-2">
                    {currentSubscriptions.map((sub) => {
                        const Icon = getSubscriptionIcon(sub.service_name, sub.category);
                        const bgColor = getCategoryColor(sub.category);
                        
                        return (
                            <div key={sub.id} className="flex justify-between items-center">
                                <div className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center mr-3`}>
                                        <Icon className="h-4 w-4 text-gray-700" />
                                    </div>
                                    <div>
                                        <div className="font-medium">{sub.service_name}</div>
                                        <div className="text-sm text-gray-500">
                                            {sub.next_billing_date ? 
                                                new Date(sub.next_billing_date).toLocaleDateString('en-US', {
                                                    month: 'long',
                                                    day: 'numeric'
                                                }) : 'Date not available'
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="font-bold">{formatCost(sub)}</div>
                            </div>
                        );
                    })}
                </div>

                {totalPages > 1 && (
                    <div className="flex justify-between items-center mt-4">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePrevPage}
                            disabled={currentPage === 0}
                            className="flex items-center"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                        </Button>
                        <span className="text-sm text-gray-500">
                            Page {currentPage + 1} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages - 1}
                            className="flex items-center"
                        >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};